import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Share2,
  Calendar,
  Users,
  MessageSquare,
  BarChart3,
  Settings,
  Plus,
  TrendingUp,
  TrendingDown,
  Clock,
  Check,
  Eye,
  RefreshCw,
  Zap,
} from 'lucide-react';
import { api } from '../../../services/api';
import { PLATFORM_CONFIG, PLATFORM_IDS, type SMCCPlatformId } from '../../../constants/smcc';

interface ContentItem {
  id: string;
  title?: string;
  content_type: string;
  status: string;
  target_platforms?: string[];
}

function QuickActionCard({
  title,
  description,
  icon: Icon,
  href,
  color = 'primary',
}: {
  title: string;
  description: string;
  icon: typeof Share2;
  href: string;
  color?: 'primary' | 'green' | 'purple' | 'amber';
}) {
  const navigate = useNavigate();
  const colorClasses = {
    primary: 'bg-primary-50 text-primary-600 group-hover:bg-primary-100',
    green: 'bg-green-50 text-green-600 group-hover:bg-green-100',
    purple: 'bg-purple-50 text-purple-600 group-hover:bg-purple-100',
    amber: 'bg-amber-50 text-amber-600 group-hover:bg-amber-100',
  };

  return (
    <button
      onClick={() => navigate(href)}
      className="group bg-white rounded-xl shadow-sm border border-gray-200 p-4 text-left hover:shadow-md transition-shadow w-full"
    >
      <div className="flex items-start gap-3">
        <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <h3 className="font-semibold text-gray-900">{title}</h3>
          <p className="text-sm text-gray-500 mt-0.5">{description}</p>
        </div>
      </div>
    </button>
  );
}

function StatCard({
  title,
  value,
  change,
  changeType = 'neutral',
  icon: Icon,
}: {
  title: string;
  value: string | number;
  change?: string;
  changeType?: 'up' | 'down' | 'neutral';
  icon: typeof Share2;
}) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm text-gray-500">{title}</span>
        <Icon className="w-4 h-4 text-gray-400" />
      </div>
      <p className="text-2xl font-bold text-gray-900">{value}</p>
      {change && (
        <div className="flex items-center gap-1 mt-1">
          {changeType === 'up' && <TrendingUp className="w-3 h-3 text-green-500" />}
          {changeType === 'down' && <TrendingDown className="w-3 h-3 text-red-500" />}
          <span
            className={`text-xs ${
              changeType === 'up'
                ? 'text-green-600'
                : changeType === 'down'
                ? 'text-red-600'
                : 'text-gray-500'
            }`}
          >
            {change}
          </span>
        </div>
      )}
    </div>
  );
}

export default function SMCCDashboard() {
  const navigate = useNavigate();

  // Fetch connected accounts
  const { data: accountsData, isLoading: loadingAccounts } = useQuery({
    queryKey: ['smcc-accounts'],
    queryFn: () => api.getSMCCAccounts(),
  });

  // Fetch content summary
  const { data: contentData, isLoading: loadingContent } = useQuery({
    queryKey: ['smcc-content-summary'],
    queryFn: () => api.getSMCCContent({ limit: 10 }),
  });

  interface SocialAccount { platform: string;
    id: string;
    status: string;
    follower_count?: number;
  }
  const accounts: SocialAccount[] = (accountsData as { accounts: SocialAccount[] } | undefined)?.accounts || [];
  const content = contentData?.content || [];

  const activeAccounts = accounts.filter((a) => a.status === 'active');
  const totalFollowers = accounts.reduce((sum, a) => sum + (a.follower_count || 0), 0);

  const scheduledCount = content.filter((c) => c.status === 'scheduled').length;
  const pendingCount = content.filter((c) => c.status === 'pending_approval').length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Social Media Control Center</h1>
          <p className="text-gray-500 mt-1">
            Manage your social media presence across all platforms
          </p>
        </div>
        <button
          onClick={() => navigate('/admin/smcc/content/new')}
          className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Create Content
        </button>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          title="Connected Accounts"
          value={activeAccounts.length}
          icon={Users}
        />
        <StatCard
          title="Total Followers"
          value={totalFollowers.toLocaleString()}
          change="+2.4% this week"
          changeType="up"
          icon={Eye}
        />
        <StatCard
          title="Scheduled Posts"
          value={scheduledCount}
          icon={Calendar}
        />
        <StatCard
          title="Pending Approval"
          value={pendingCount}
          change={pendingCount > 0 ? 'Needs attention' : undefined}
          changeType={pendingCount > 0 ? 'down' : 'neutral'}
          icon={Clock}
        />
      </div>

      {/* Quick Actions */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-3">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <QuickActionCard
            title="Content Calendar"
            description="View and schedule posts"
            icon={Calendar}
            href="/admin/smcc/calendar"
            color="primary"
          />
          <QuickActionCard
            title="Manage Accounts"
            description="Connect social platforms"
            icon={Users}
            href="/admin/smcc/accounts"
            color="green"
          />
          <QuickActionCard
            title="Engagement Inbox"
            description="Respond to messages"
            icon={MessageSquare}
            href="/admin/smcc/inbox"
            color="purple"
          />
          <QuickActionCard
            title="Analytics"
            description="View performance metrics"
            icon={BarChart3}
            href="/admin/smcc/analytics"
            color="amber"
          />
        </div>
      </div>

      {/* Connected Accounts Overview */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Connected Accounts</h2>
          <button
            onClick={() => navigate('/admin/smcc/accounts')}
            className="text-sm text-primary-600 hover:text-primary-700"
          >
            View all
          </button>
        </div>

        {loadingAccounts ? (
          <div className="flex items-center justify-center py-8">
            <RefreshCw className="w-6 h-6 text-gray-400 animate-spin" />
          </div>
        ) : activeAccounts.length === 0 ? (
          <div className="text-center py-8">
            <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 mb-4">No accounts connected yet</p>
            <button
              onClick={() => navigate('/admin/smcc/accounts')}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
            >
              Connect Account
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
            {PLATFORM_IDS.map((platform: SMCCPlatformId) => {
              const platformAccounts = activeAccounts.filter((a) => a.platform === platform);
              const config = PLATFORM_CONFIG[platform];
              const hasAccount = platformAccounts.length > 0;
              const followers = platformAccounts.reduce((sum, a) => sum + (a.follower_count || 0), 0);

              return (
                <div
                  key={platform}
                  className={`p-3 rounded-lg border ${
                    hasAccount ? 'border-gray-200 bg-white' : 'border-dashed border-gray-300 bg-gray-50'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <div
                      className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold"
                      style={{ backgroundColor: hasAccount ? config.color : '#9ca3af' }}
                    >
                      {config.name[0]}
                    </div>
                    <span className="text-sm font-medium text-gray-900 truncate">
                      {config.name}
                    </span>
                  </div>
                  {hasAccount ? (
                    <div className="text-xs text-gray-500">
                      <p>{platformAccounts.length} account{platformAccounts.length !== 1 ? 's' : ''}</p>
                      <p className="font-medium text-gray-700">{followers.toLocaleString()} followers</p>
                    </div>
                  ) : (
                    <button
                      onClick={() => navigate('/admin/smcc/accounts')}
                      className="text-xs text-primary-600 hover:text-primary-700"
                    >
                      + Connect
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Recent Content */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Recent Content</h2>
          <button
            onClick={() => navigate('/admin/smcc/calendar')}
            className="text-sm text-primary-600 hover:text-primary-700"
          >
            View calendar
          </button>
        </div>

        {loadingContent ? (
          <div className="flex items-center justify-center py-8">
            <RefreshCw className="w-6 h-6 text-gray-400 animate-spin" />
          </div>
        ) : content.length === 0 ? (
          <div className="text-center py-8">
            <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 mb-4">No content created yet</p>
            <button
              onClick={() => navigate('/admin/smcc/content/new')}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
            >
              Create Content
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {content.slice(0, 5).map((item: ContentItem) => (
              <button
                type="button"
                key={item.id}
                className="w-full flex items-center justify-between p-3 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer"
                onClick={() => navigate(`/admin/smcc/content/${item.id}`)}
              >
                <div className="flex items-center gap-3">
                  <div className="flex -space-x-1">
                    {item.target_platforms?.slice(0, 3).map((platform: string) => (
                      <div
                        key={platform}
                        className="w-6 h-6 rounded-full border-2 border-white flex items-center justify-center text-white text-[10px] font-bold"
                        style={{ backgroundColor: PLATFORM_CONFIG[platform as SMCCPlatformId]?.color || '#666' }}
                      >
                        {PLATFORM_CONFIG[platform as SMCCPlatformId]?.name[0] || '?'}
                      </div>
                    ))}
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{item.title || 'Untitled'}</p>
                    <p className="text-xs text-gray-500 capitalize">{item.content_type}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span
                    className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                      item.status === 'published'
                        ? 'bg-green-100 text-green-700'
                        : item.status === 'scheduled'
                        ? 'bg-purple-100 text-purple-700'
                        : item.status === 'pending_approval'
                        ? 'bg-amber-100 text-amber-700'
                        : 'bg-gray-100 text-gray-700'
                    }`}
                  >
                    {item.status}
                  </span>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Feature Status */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl border border-green-200 p-4">
          <div className="flex items-center gap-2 mb-2">
            <Check className="w-5 h-5 text-green-600" />
            <span className="font-semibold text-green-800">Active Features</span>
          </div>
          <ul className="text-sm text-green-700 space-y-1">
            <li>Account Management</li>
            <li>Content Calendar & Scheduling</li>
            <li>AI Content Generation</li>
            <li>Brand Voice Configuration</li>
            <li>Compliance Rules & Kill Switch</li>
            <li>Campaign Management</li>
          </ul>
        </div>

        <div className="bg-gradient-to-br from-amber-50 to-yellow-50 rounded-xl border border-amber-200 p-4">
          <div className="flex items-center gap-2 mb-2">
            <Zap className="w-5 h-5 text-amber-600" />
            <span className="font-semibold text-amber-800">Ready (Need OAuth Setup)</span>
          </div>
          <ul className="text-sm text-amber-700 space-y-1">
            <li>Auto-Publishing to Platforms</li>
            <li>Engagement Inbox Sync</li>
            <li>Analytics Data Sync</li>
          </ul>
        </div>

        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-200 p-4">
          <div className="flex items-center gap-2 mb-2">
            <Settings className="w-5 h-5 text-blue-600" />
            <span className="font-semibold text-blue-800">Platforms Supported</span>
          </div>
          <ul className="text-sm text-blue-700 space-y-1">
            <li>Instagram, Facebook, Twitter/X</li>
            <li>TikTok, YouTube, LinkedIn</li>
            <li>Pinterest</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
